import type { GameEvent, MatchEvent, Phase, PlayerId } from '../core';
import { bonusInWave, bonusVoiceAt } from './bonusVoice';

/**
 * Announcer lines. One line at a time. If any line is already playing, the new
 * request is dropped: it is not queued, and it does not speak over the current one.
 * "Bonus turn!" finishes even when a result, a ready call, or an exclamation
 * arrives while it is speaking.
 *
 * Line 17 (Final round) is unwired: a match has turns and a sudden-death tiebreak,
 * not a final round. Lines 10–12 were not in the set. vo-05-clatter-tastic is retired.
 * Nice and Sweet take 2 stay on disk and are not in the ladder.
 */
export const EXCLAMATION_ORDER = [
  'nice',
  'sweet',
  'incredible',
  'amazing',
  'fantastic',
  'spectacular',
  'awesome',
] as const;

export type ExclamationId = (typeof EXCLAMATION_ORDER)[number];

/**
 * The ladder resets when a match begins, not when a turn passes.
 * A duel is one match, so the climb (Nice through Awesome) should span the glen
 * and wrap if the duel is long. A rematch starts back at Nice. Resetting every
 * turn would replay Nice on almost every turn, because most turns have one big moment.
 */
export const EXCLAMATION_RESET = 'match' as const;

const EXCLAMATION_FILE: Record<ExclamationId, string> = {
  nice: 'vo-03-nice-take1',
  sweet: 'vo-04-sweet-take1',
  incredible: 'vo-05c-incredible-bright',
  amazing: 'vo-05b-amazing-bright',
  fantastic: 'vo-05f-fantastic-bright',
  spectacular: 'vo-05d-spectacular-bright',
  awesome: 'vo-05e-awesome-bright',
};

/** Shared rank for the cascade ladder. A line already playing is never replaced. */
const EXCLAMATION_PRIORITY = 45;

/** Same rank as the older win and loss lines, so a second result line is skipped rather than stacked. */
const RESULT_PRIORITY = 100;

/**
 * The sixteen result-screen clips. 19a and 19b stay as `so-close` and `next-time`
 * and are mixed into the loss pools below, not duplicated here.
 */
export const RESULT_CLIP_FILES = {
  'cl-next-time': 'vo-result-cl-1-next-time',
  'cl-so-close': 'vo-result-cl-2-so-close',
  'cl-nail-biter': 'vo-result-cl-3-nail-biter',
  'cl-one-move-away': 'vo-result-cl-4-one-move-away',
  'bl-totally-destroyed': 'vo-result-bl-1-totally-destroyed',
  'bl-wipeout': 'vo-result-bl-2-wipeout',
  'bl-didnt-go-well': 'vo-result-bl-3-didnt-go-well',
  'bl-shake-it-off': 'vo-result-bl-4-shake-it-off',
  'cw-squeaked-it-out': 'vo-result-cw-1-squeaked-it-out',
  'cw-what-a-finish': 'vo-result-cw-2-what-a-finish',
  'cw-close-but-you-did-it': 'vo-result-cw-3-close-but-you-did-it',
  'cw-down-to-the-wire': 'vo-result-cw-4-down-to-the-wire',
  'bw-total-domination': 'vo-result-bw-1-total-domination',
  'bw-flawless-victory': 'vo-result-bw-2-flawless-victory',
  'bw-you-crushed-it': 'vo-result-bw-3-you-crushed-it',
  'bw-unstoppable': 'vo-result-bw-4-unstoppable',
} as const;

export type ResultClipId = keyof typeof RESULT_CLIP_FILES;

export const RESULT_BUCKETS = ['cl', 'bl', 'cw', 'bw'] as const;
export type ResultBucket = (typeof RESULT_BUCKETS)[number];

export type VoiceId =
  | ExclamationId
  | ResultClipId
  | 'your-turn'
  | 'their-move'
  | 'bee-bomb'
  | 'rainbow'
  | 'power-ready'
  | 'bristle-roll'
  | 'trick-ready'
  | 'blocked'
  | 'rooted'
  | 'last-move'
  | 'you-win'
  | 'so-close'
  | 'next-time'
  | 'draw'
  | 'pick-critter'
  | 'get-ready'
  | 'bonus-turn';

/**
 * Close means the score gap is at most 15% of the winner's score.
 * Compared as gap / winner <= 3/20, without dividing, so an exact 15% stays close.
 * A zero winner score is close only when the gap is also zero.
 */
export function isCloseResult(scores: readonly [number, number], winner: 0 | 1): boolean {
  const won = scores[winner];
  const lost = scores[winner === 0 ? 1 : 0];
  return (won - lost) * 20 <= won * 3;
}

export function resultBucket(scores: readonly [number, number], winner: 0 | 1): ResultBucket {
  const close = isCloseResult(scores, winner);
  if (winner === 0) return close ? 'cw' : 'bw';
  return close ? 'cl' : 'bl';
}

const ROTATING = ['your-turn'] as const;
type RotatingId = (typeof ROTATING)[number];

const RESULT_CLIP_PRIORITY = Object.fromEntries(
  (Object.keys(RESULT_CLIP_FILES) as ResultClipId[]).map((id) => [id, RESULT_PRIORITY]),
) as Record<ResultClipId, number>;

export const VOICE_PRIORITY: Record<VoiceId, number> = {
  'you-win': RESULT_PRIORITY,
  'so-close': RESULT_PRIORITY,
  'next-time': RESULT_PRIORITY,
  ...RESULT_CLIP_PRIORITY,
  draw: 100,
  'bristle-roll': 90,
  'get-ready': 80,
  'power-ready': 75,
  'trick-ready': 75,
  blocked: 70,
  rooted: 70,
  rainbow: 65,
  'bee-bomb': 65,
  'last-move': 55,
  'bonus-turn': 50,
  nice: EXCLAMATION_PRIORITY,
  sweet: EXCLAMATION_PRIORITY,
  incredible: EXCLAMATION_PRIORITY,
  amazing: EXCLAMATION_PRIORITY,
  fantastic: EXCLAMATION_PRIORITY,
  spectacular: EXCLAMATION_PRIORITY,
  awesome: EXCLAMATION_PRIORITY,
  'your-turn': 40,
  'their-move': 40,
  'pick-critter': 20,
};

/** File base names under /audio, without the extension. Takes are in order. */
export const VOICE_FILES: Record<VoiceId, readonly string[]> = {
  'your-turn': ['vo-01-your-turn-take1', 'vo-01-your-turn-take2'],
  'their-move': ['vo-02-their-move'],
  nice: [EXCLAMATION_FILE.nice],
  sweet: [EXCLAMATION_FILE.sweet],
  incredible: [EXCLAMATION_FILE.incredible],
  amazing: [EXCLAMATION_FILE.amazing],
  fantastic: [EXCLAMATION_FILE.fantastic],
  spectacular: [EXCLAMATION_FILE.spectacular],
  awesome: [EXCLAMATION_FILE.awesome],
  'bee-bomb': ['vo-06-bee-bomb'],
  rainbow: ['vo-07-rainbow'],
  'power-ready': ['vo-08-power-ready'],
  'bristle-roll': ['vo-09-bristle-roll'],
  'trick-ready': ['vo-13-trick-ready'],
  blocked: ['vo-14-blocked'],
  rooted: ['vo-15-rooted'],
  'last-move': ['vo-16-last-move'],
  'you-win': ['vo-18-you-win'],
  'so-close': ['vo-19a-so-close'],
  'next-time': ['vo-19b-next-time'],
  ...resultClipFiles(),
  draw: ['vo-20-draw'],
  'pick-critter': ['vo-21-pick-your-critter'],
  'get-ready': ['vo-22-get-ready-go'],
  'bonus-turn': ['vo-bonus-turn-bright'],
};

/**
 * One line for the result screen, from the bucket for that score.
 *
 * 19a "So close" sits in the close-loss pool. The words are about a small gap.
 * 19b "Next time" sits in the big-loss pool. It does not mention the margin, and
 * close loss already has its own "next time" clip (vo-result-cl-1-next-time).
 * Both stay in a pool so they still play. The screen speaks one of these lines,
 * not a legacy line as well.
 *
 * vo-18 "You win" is not in a win pool. Playing it beside a close-win or big-win
 * clip would stack two result lines.
 */
export const RESULT_POOLS: Record<ResultBucket, readonly VoiceId[]> = {
  cl: ['cl-next-time', 'cl-so-close', 'cl-nail-biter', 'cl-one-move-away', 'so-close'],
  bl: ['bl-totally-destroyed', 'bl-wipeout', 'bl-didnt-go-well', 'bl-shake-it-off', 'next-time'],
  cw: ['cw-squeaked-it-out', 'cw-what-a-finish', 'cw-close-but-you-did-it', 'cw-down-to-the-wire'],
  bw: ['bw-total-domination', 'bw-flawless-victory', 'bw-you-crushed-it', 'bw-unstoppable'],
};

export function isResultLine(id: VoiceId): boolean {
  return (Object.values(RESULT_POOLS) as readonly (readonly VoiceId[])[]).some((pool) => pool.includes(id));
}

/**
 * A uniform pick from the pool, skipping the clip that just played.
 * A one-clip pool would have to repeat; every current pool is larger than that.
 */
export function pickFromPool(pool: readonly VoiceId[], last: VoiceId | null, random: () => number): VoiceId {
  const available = pool.filter((id) => id !== last);
  const choices = available.length > 0 ? available : pool;
  const roll = random();
  const index = Math.min(choices.length - 1, Math.max(0, Math.floor(roll * choices.length)));
  return choices[index];
}

function resultClipFiles(): Record<ResultClipId, readonly string[]> {
  const files = {} as Record<ResultClipId, readonly string[]>;
  for (const id of Object.keys(RESULT_CLIP_FILES) as ResultClipId[]) files[id] = [RESULT_CLIP_FILES[id]];
  return files;
}

/** Shipped, but no trigger in the game. Clatter-tastic is retired. Nice/Sweet take 2 are spare. */
export const UNWIRED_VOICE_FILES = [
  'vo-17-final-round',
  'vo-05-clatter-tastic',
  'vo-03-nice-take2',
  'vo-04-sweet-take2',
] as const;

export function isExclamation(id: VoiceId): id is ExclamationId {
  return (EXCLAMATION_ORDER as readonly string[]).includes(id);
}

export function voicePriority(id: VoiceId): number {
  return VOICE_PRIORITY[id];
}

/**
 * Whether the incoming line may start. The speaker holds one line.
 * Anything already playing, including "Bonus turn!", keeps the speaker.
 * The incoming line is dropped. It is not queued and it does not overlap.
 */
export function voiceContest(current: VoiceId | null, incoming: VoiceId): 'play' | 'skip' {
  if (current !== null) return 'skip';
  return VOICE_FILES[incoming].length > 0 ? 'play' : 'skip';
}

export function voiceFile(id: VoiceId, take = 0): string {
  const files = VOICE_FILES[id];
  return files[take] ?? files[0];
}

/** Bright accents ship as ogg + m4a and replace the hotter originals. */
export function isBrightVoice(file: string): boolean {
  return file.endsWith('-bright');
}

/**
 * mp3 first. ogg only leads when the browser will not decode mp3.
 * Bright files have no mp3: ogg leads and m4a is the fallback.
 */
export function voiceSources(
  file: string,
  support: { mp3: boolean; ogg: boolean; m4a?: boolean },
): string[] {
  if (isBrightVoice(file)) {
    const ogg = support.ogg !== false;
    const m4a = support.m4a !== false;
    const oggUrl = `/audio/${file}.ogg`;
    const m4aUrl = `/audio/${file}.m4a`;
    if (ogg && m4a) return [oggUrl, m4aUrl];
    if (ogg) return [oggUrl];
    if (m4a) return [m4aUrl];
    return [oggUrl, m4aUrl];
  }
  const mp3 = `/audio/${file}.mp3`;
  const ogg = `/audio/${file}.ogg`;
  if (support.ogg && !support.mp3) return [ogg, mp3];
  return [mp3, ogg];
}

function isRotating(id: VoiceId): id is RotatingId {
  return id === 'your-turn';
}

/** A clear bigger than a match-3 on the opening wave. Later waves speak on the cascade event. */
function isBigMoveMatch(event: MatchEvent): boolean {
  return event.size > 3 && event.cascade === 0;
}

/**
 * Which line a board event asks for. Fill memory lives here so "Power ready"
 * and "Trick ready" speak once per fill, and only for the human.
 * Exclamations walk EXCLAMATION_ORDER. A bonus beat never asks for one.
 */
export class VoiceDirector {
  meterMax = 60;
  private takes: Record<RotatingId, number> = { 'your-turn': 0 };
  private meterReady = false;
  private trickReady: [boolean, boolean] = [false, false];
  private lastMoveOn = false;
  /** Next ladder step. Counts committed lines, so a skipped overlap does not skip a word. */
  private exclamationAt = 0;
  /** Index already chosen for the line that has not committed yet. */
  private exclamationPending: number | null = null;
  /** Last result clip that actually started. Survives a rematch so the next screen does not repeat it. */
  private lastResult: VoiceId | null = null;

  constructor(private readonly random: () => number = Math.random) {}

  /** A new match starts the ladder over. Take rotation and the last result clip keep going. */
  beginMatch(meterMax: number): void {
    this.meterMax = meterMax;
    this.meterReady = false;
    this.trickReady = [false, false];
    this.lastMoveOn = false;
    this.exclamationAt = 0;
    this.exclamationPending = null;
  }

  peekTake(id: VoiceId): number {
    if (!isRotating(id)) return 0;
    return this.takes[id];
  }

  /**
   * The one result line for this screen. Hearing it (commit) is what blocks a repeat.
   * A peek that never starts does not consume the clip.
   */
  pickResult(scores: readonly [number, number], winner: 0 | 1): VoiceId {
    return pickFromPool(RESULT_POOLS[resultBucket(scores, winner)], this.lastResult, this.random);
  }

  /** Call only when the line actually starts, not when overlap drops it. */
  commit(id: VoiceId): void {
    if (isExclamation(id)) {
      const index = EXCLAMATION_ORDER.indexOf(id);
      this.exclamationAt = (index >= 0 ? index : this.exclamationAt) + 1;
      this.exclamationPending = null;
      return;
    }
    if (isRotating(id)) this.takes[id] = (this.takes[id] + 1) % VOICE_FILES[id].length;
    if (isResultLine(id)) this.lastResult = id;
  }

  fromEvent(event: GameEvent, events: readonly GameEvent[], index: number): VoiceId | null {
    switch (event.type) {
      case 'match':
      case 'bonus-move':
        if (bonusVoiceAt(events, index)) return 'bonus-turn';
        if (event.type === 'match' && isBigMoveMatch(event)) return this.peekExclamation();
        return null;
      case 'cascade':
        if (event.multiplier >= 2 && !bonusInWave(events, index)) return this.peekExclamation();
        return null;
      case 'special-created':
        if (event.special === 'bomb') return 'bee-bomb';
        if (event.special === 'rainbow') return 'rainbow';
        return null;
      case 'meter':
        return this.meterFilled(event.player, event.delta, event.total);
      case 'powerup-charge':
        return this.trickFilled(event.player, event.slot, event.delta, event.total, event.cost);
      case 'power':
        return event.heroId === 'pip' ? 'bristle-roll' : null;
      case 'powerup':
        return event.blocked ? 'blocked' : null;
      case 'status':
        if (event.kind === 'locked' && event.change === 'added' && event.owner === 0) return 'rooted';
        return null;
      case 'turn-ended':
        return event.nextPlayer === 0 ? 'your-turn' : 'their-move';
      default:
        return null;
    }
  }

  /**
   * The human can still spend a move, and they have exactly one.
   * Stays quiet while that remains true, then arms again once it changes.
   */
  fromMoves(currentPlayer: PlayerId, movesLeft: number, phase: Phase): VoiceId | null {
    const armed = phase !== 'ended' && currentPlayer === 0 && movesLeft === 1;
    if (!armed) {
      this.lastMoveOn = false;
      return null;
    }
    if (this.lastMoveOn) return null;
    this.lastMoveOn = true;
    return 'last-move';
  }

  private peekExclamation(): ExclamationId {
    if (this.exclamationPending === null) {
      const count = EXCLAMATION_ORDER.length;
      this.exclamationPending = ((this.exclamationAt % count) + count) % count;
    }
    return EXCLAMATION_ORDER[this.exclamationPending];
  }

  private meterFilled(player: PlayerId, delta: number, total: number): VoiceId | null {
    if (player !== 0) return null;
    const full = delta > 0 && total >= this.meterMax;
    const was = this.meterReady;
    this.meterReady = total >= this.meterMax;
    if (full && !was) return 'power-ready';
    return null;
  }

  private trickFilled(player: PlayerId, slot: number, delta: number, total: number, cost: number): VoiceId | null {
    if (player !== 0 || (slot !== 0 && slot !== 1)) return null;
    const full = delta > 0 && total >= cost;
    const was = this.trickReady[slot];
    this.trickReady[slot] = total >= cost;
    if (full && !was) return 'trick-ready';
    return null;
  }
}
